import type {
  AnswerMessage,
  AnyMessage,
  AnyRealtimeMessage,
  Attachment,
  BotQuestionMessage,
  LivechatMessage,
  Message,
  ReviewMessage,
  SelectionsMessage,
  StatusMessage,
  TimeSlot,
  ImageMessage,
} from '@/types/app'

import type {
  AnswerMessageResponse,
  Attachment as AttachmentResponse,
  BotQuestionMessageResponse,
  DeserializedAnswer,
  LivechatMessageResponse,
  MessageResponse,
  NewMessageResponse,
  Question,
  QuestionMessageResponse,
  RealtimeMessageResponse,
  ReviewMessageResponse,
  StatusMessageResponse,
  TimeSlot as TimeSlotResponse,
} from '@/apis/repositories/models/cinnamon'

import { getAPIBaseURL } from '@/config'

const parseTimeSlot = (ts: TimeSlotResponse): TimeSlot => ({
  id: ts.id,
  startsAt: new Date(ts.starts_at),
  endsAt: new Date(ts.ends_at),
  isAvailable: ts.is_available,
})

const convertAttachment = (attachment: AttachmentResponse): Attachment => {
  switch (attachment.type) {
    case 'bookingRequest':
      return {
        type: attachment.type,
        content: {
          id: attachment.content.id,
          fallbackUrl: attachment.content.fallback_url,
          timeSlots: attachment.content.time_slots.map(parseTimeSlot),
        },
      }
    case 'bookingAnswer':
      return {
        type: attachment.type,
        content: parseTimeSlot(attachment.content.time_slot),
      }
    case 'insuranceProductDocumentRequest':
      return {
        type: attachment.type,
        content: {
          id: attachment.content.id,
          productIds: attachment.content.product_ids,
        },
      }
    case 'insuranceProductConsultationRequest':
      return {
        type: attachment.type,
        content: {
          id: attachment.content.id,
          productId: attachment.content.product_id,
          companyId: attachment.content.company_id,
        },
      }
  }
}
const parseAttachments = (attachments: AttachmentResponse[]): Attachment[] =>
  attachments.map(a => convertAttachment(a))

const parseMessage = (message: MessageResponse): Message => ({
  id: message.id,
  guid: message.guid,
  sender: {
    kind: message.chat_participant_kind,
    chatParticipantId: message.chat_participant_id,
  },
  body: message.message,
  type: 'normal',
  createdAt: new Date(message.created_at),
  attachments: parseAttachments(message.attachments ?? []),
})

const parseImageMessage = (message: MessageResponse): ImageMessage => {
  // 画像メッセージを送信できるのはプランナーのみ
  if (message.chat_participant_kind === 'user') {
    throw new Error('User cannot send image message')
  }

  const imgixDomain = new URL(getAPIBaseURL('imgix')).host

  return {
    id: message.id,
    guid: message.guid,
    sender: {
      kind: message.chat_participant_kind,
      chatParticipantId: message.chat_participant_id,
    },
    // チャットとして送信された画像の保管庫 `chat_message_images` は cinnamon の管轄だが、
    // 滅多に変わることが無いと考えられるため例外的にハードコーディングしている
    contentURL: `https://${imgixDomain}/chat_message_images/${message.message}`,
    type: 'image',
    createdAt: new Date(message.created_at),
    attachments: parseAttachments(message.attachments ?? []),
  }
}

export const parseSelectionsMessage = (
  message: QuestionMessageResponse,
  question: Question
): SelectionsMessage => {
  // 質問メッセージを送信できるのはプランナーのみ
  if (message.chat_participant_kind === 'user') {
    throw new Error('User cannot send selection message')
  }

  return {
    id: message.id,
    guid: message.guid,
    sender: {
      kind: message.chat_participant_kind,
      chatParticipantId: message.chat_participant_id,
    },
    body: message.message,
    type: 'selections',
    selections: question.answer_selections.map(
      selection => selection.selection
    ),
    createdAt: new Date(message.created_at),
    attachments: parseAttachments(message.attachments ?? []),
  }
}

export const parseBotQuestionMessage = (
  message: BotQuestionMessageResponse,
  question: Question
): BotQuestionMessage => ({
  id: message.id,
  guid: message.guid,
  body: message.message,
  type: 'bot_question',
  selections: question.answer_selections.map(selection => selection.selection),
  createdAt: new Date(message.created_at),
  attachments: parseAttachments(message.attachments ?? []),
  sender: {
    kind: 'bot',
  },
})

const parseLivechatMessage = (
  message: LivechatMessageResponse
): LivechatMessage => ({
  id: message.id,
  guid: message.guid,
  chatParticipantId: message.chat_participant_id,
  status: message.message,
  type: 'livechat',
  createdAt: new Date(message.created_at),
  attachments: parseAttachments(message.attachments ?? []),
  sender: {
    kind: 'system',
  },
})

const parseStatusMessage = (message: StatusMessageResponse): StatusMessage => ({
  id: 0,
  guid: '',
  chatParticipantId: message.chat_participant_id,
  type: message.message_type,
  createdAt: new Date(),
  attachments: parseAttachments([]),
})

const parseMessageResponse = (
  message: Exclude<
    MessageResponse,
    ReviewMessageResponse | QuestionMessageResponse | BotQuestionMessageResponse
  >
): Exclude<AnyMessage, ReviewMessage> => {
  switch (message.message_type) {
    case 'normal':
      return parseMessage(message)
    case 'livechat':
      return parseLivechatMessage(message)
    case 'answer':
      return parseAnswerMessage(message)
    case 'image':
      return parseImageMessage(message)
  }
}

const parseRealtimeMessage = (
  message: Exclude<
    RealtimeMessageResponse,
    | ReviewMessageResponse
    | QuestionMessageResponse
    | BotQuestionMessageResponse
    | NewMessageResponse
  >
): Exclude<AnyRealtimeMessage, ReviewMessage> => {
  switch (message.message_type) {
    case 'normal':
    case 'livechat':
    case 'answer':
    case 'image':
      return parseMessageResponse(message)
    case 'typing':
    case 'deleted':
      return parseStatusMessage(message)
  }
}

export const parseAnyMessageExcludingReview = (
  message: Exclude<
    MessageResponse,
    ReviewMessageResponse | QuestionMessageResponse | BotQuestionMessageResponse
  >
): Exclude<AnyMessage, ReviewMessage> => parseMessageResponse(message)

export const parseAnyRealtimeMessageExcludingReview = (
  message: Exclude<
    RealtimeMessageResponse,
    | ReviewMessageResponse
    | QuestionMessageResponse
    | BotQuestionMessageResponse
    | NewMessageResponse
  >
): Exclude<AnyRealtimeMessage, ReviewMessage> => parseRealtimeMessage(message)

export const parseReviewMessage = (
  message: ReviewMessageResponse
): ReviewMessage => {
  return {
    id: message.id,
    guid: message.guid,
    type: 'review',
    sender: {
      kind: 'user',
      chatParticipantId: 0,
    },
    createdAt: new Date(message.created_at),
    attachments: [],
  }
}

export const parseAnswerMessage = (
  message: AnswerMessageResponse
): AnswerMessage => {
  const answer: DeserializedAnswer = JSON.parse(message.message)

  return {
    id: message.id,
    guid: message.guid,
    type: 'answer',
    selectedAnswers: answer.selected_answer_selections,
    chatParticipantId: message.chat_participant_id,
    createdAt: new Date(message.created_at),
    attachments: [],
    sender: {
      kind: 'user',
      chatParticipantId: message.chat_participant_id,
    },
  }
}
